import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Briefcase, GraduationCap, Rocket, Building, MessageCircle, ExternalLink, Music, Camera, Users, Store, Palette, Mic } from "lucide-react";

const ROLES = [
  { icon: Briefcase, title: "Part Time", desc: "Flexible gigs at events near you — check-ins, hospitality, promo." },
  { icon: GraduationCap, title: "Campus Ambassador", desc: "Rep Eventom at your college. Perks, points, and the room after the room." },
  { icon: Rocket, title: "Internship", desc: "Ship real product with the team. Design, engineering, growth, ops." },
  { icon: Building, title: "Full Time", desc: "Build the future of live entertainment for Bharat. Remote-friendly." },
];

const ECOSYSTEM = [
  { icon: Music, title: "Artist", genre: "artist", desc: "Perform at concerts, festivals & house parties across India." },
  { icon: Users, title: "Creator", genre: "creator", desc: "Cover events, amplify launches, host live activations." },
  { icon: Store, title: "Vendor", genre: "vendor", desc: "Sound, lights, catering, décor — power the show." },
  { icon: Camera, title: "Photographer", genre: "photographer", desc: "Shoot festivals & concerts. Land recurring gigs." },
  { icon: Palette, title: "Agency", genre: "agency", desc: "Take full-stack briefs from India's biggest brands." },
  { icon: Mic, title: "Freelancer", genre: "freelancer", desc: "Anchors, MCs, DJs, editors — pick your slots." },
];

const APPLY_URL = "https://tally.so/r/VLylzM";
const WA_URL = "https://wa.me/917250120666?text=Hi%20Eventom%20—%20I'd%20like%20to%20apply";

export default function WorkWithEventom() {
  return (
    <Layout>
      <section className="relative overflow-hidden py-16 border-b border-line" style={{ background: "radial-gradient(ellipse at top, rgba(170,238,0,0.25), transparent 60%), radial-gradient(ellipse at bottom right, rgba(123,47,255,0.35), transparent 60%), #0A0A0A" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="chip bg-lime/20 text-lime mb-3">Work with Eventom</div>
          <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">Build the <span className="grad-text-hot">live culture</span> of Bharat.</h1>
          <p className="mt-3 text-text/85 max-w-2xl">Join the team, or plug in as a partner. Ship for millions of fans, organisers, brands and creators.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="chip !text-lime !border-lime/40 mb-3">On the team</div>
        <h2 className="font-display text-3xl md:text-4xl mb-6">Full-time. Interns. Ambassadors.</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {ROLES.map((r) => (
            <a key={r.title} href={APPLY_URL} target="_blank" rel="noopener noreferrer" className="glass rounded-3xl p-6 card-lift block" data-testid={`role-${r.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="icon-circle bg-pink/10 text-pink !w-12 !h-12 mb-3"><r.icon className="w-5 h-5" /></div>
              <div className="font-display uppercase text-2xl">{r.title}</div>
              <div className="text-sm text-muted mt-2">{r.desc}</div>
              <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-lime inline-flex items-center gap-1">Apply <ExternalLink className="w-3 h-3" /></div>
            </a>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <a href={APPLY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="work-apply-cta"><Rocket className="w-4 h-4" /> Apply Now</a>
          <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="btn-lime" data-testid="work-whatsapp-cta"><MessageCircle className="w-4 h-4" /> WhatsApp Us</a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 border-t border-line" data-testid="work-ecosystem-section">
        <div className="chip !text-skyx !border-skyx/40 mb-3">Ecosystem partners</div>
        <h2 className="font-display text-3xl md:text-4xl mb-2">Get booked, get paid.</h2>
        <p className="text-sm text-muted mb-6 max-w-xl">List once as an artist, creator, vendor, agency or freelancer — organisers and brands book you directly.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ECOSYSTEM.map((r) => (
            <Link key={r.title} to={`/ecosystem?genre=${r.genre}`} className="glass rounded-3xl p-6 card-lift block" data-testid={`work-eco-${r.genre}`}>
              <div className="icon-circle bg-lime/10 text-lime !w-12 !h-12 mb-3"><r.icon className="w-5 h-5" /></div>
              <div className="font-display uppercase text-2xl">{r.title}</div>
              <div className="text-sm text-muted mt-2">{r.desc}</div>
              <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-skyx inline-flex items-center gap-1">List your service <ExternalLink className="w-3 h-3" /></div>
            </Link>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/ecosystem#list" className="btn-ghost" data-testid="work-eco-cta"><Users className="w-4 h-4" /> Browse all ecosystem opportunities</Link>
        </div>
      </section>
    </Layout>
  );
}
